import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Text from "../../../common/typography/text.js";
import Button from "../../../common/button/button.js";
import Panel from "../../../common/panel/index.js";
import ChangeIndicator from "../../../common/change-indicator/change-indicator.js";
import AABIErrorPanel from "./AABIErrorPanel.js";
import RankedListSkeleton from "./RankedListSkeleton.js";
import { fetchTopEntities } from "../aabiService";
import { buildInvoiceDashboardUrl } from "../aabiFilters";
import {
  DIMENSIONS,
  DIMENSION_LABELS,
  MEASURE_METRIC_KEY,
} from "../aabiConstants";
import { formatGrowth, growthVariant } from "../aabiFormatters";
import CompactCurrency from "./CompactCurrency";
import "./../AABI.css";

const DEFAULT_LIMIT = 5;
const EXPANDED_LIMIT = 25; // bounded expanded view (§6.4) — never cumulative offset paging

// Invoice-dashboard URL param that each leaders dimension filters by.
const ROW_FILTER_KEY = {
  [DIMENSIONS.ACCOUNTS]: "accountId",
  [DIMENSIONS.STATES]: "stateId",
  [DIMENSIONS.DISTRIBUTORS]: "distributorId",
};

const EMPTY_MESSAGE = {
  [DIMENSIONS.ACCOUNTS]: "No accounts contributed revenue in this period.",
  [DIMENSIONS.STATES]: "No states contributed revenue in this period.",
  [DIMENSIONS.DISTRIBUTORS]: "No distributors contributed revenue in this period.",
};

const DEFAULT_DIMENSIONS = Object.values(DIMENSIONS).map((key) => ({
  key,
  label: DIMENSION_LABELS[key].replace(/^Top /, ""),
  errorLabel: DIMENSION_LABELS[key],
}));

// Leaders panel (§6.4): replaces the three simultaneous top panels with one
// tabbed panel using the same timeframe-button switcher convention as the
// page's other tab controls. Only the active dimension is fetched, then cached
// per scope/period/dimension; the dual-ranking response (§8.4) re-ranks for
// the selected measure client-side without refetching. Row typography follows
// the QuickList conventions (tinyText, ChangeIndicator).
//
// Additive props for the Executive Dashboard (defaults keep the manufacturer
// page unchanged):
//   dimensions     [{ key, label }] tabs; default Accounts, States, Distributors
//   fetcher        replaces fetchTopEntities; gets { ...scope, dimension, ... }
//   buildRowLink   (row, dimension) => url | null; replaces the invoice link
//   emptyMessages  { [dimension]: text }
//   refreshKey     changing it forces a refetch (toolbar Refresh)
//   onLoaded       called with the load time after every successful fetch
const LeadersPanel = ({
  scope,
  period,
  measure,
  totals,
  dimensions = DEFAULT_DIMENSIONS,
  fetcher = fetchTopEntities,
  buildRowLink,
  emptyMessages = EMPTY_MESSAGE,
  refreshKey = 0,
  onLoaded,
}) => {
  const [activeDimension, setActiveDimension] = useState(dimensions[0].key);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rankings, setRankings] = useState(null);
  const requestIdRef = useRef(0);

  const metricKey = MEASURE_METRIC_KEY[measure];

  // A hidden tab (the Regions tab, OD11) must never stay selected.
  useEffect(() => {
    if (!dimensions.some((dimension) => dimension.key === activeDimension)) {
      setActiveDimension(dimensions[0].key);
    }
  }, [dimensions, activeDimension]);

  const scopeKey = [
    JSON.stringify(scope),
    period.startDate,
    period.endDate,
    activeDimension,
  ].join("|");

  // Cache keyed by scope/period/dimension (§8.6). A measure change must NOT
  // refetch — the dual-ranking payload already contains both orders.
  const cacheRef = useRef(new Map());
  const cacheKey = `${scopeKey}|${expanded ? EXPANDED_LIMIT : DEFAULT_LIMIT}`;

  const loadData = async ({ force = false } = {}) => {
    if (!force) {
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        setRankings(cached.rankings);
        setError(false);
        setLoading(false);
        return;
      }
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const data = await fetcher({
        ...scope,
        dimension: activeDimension,
        startDate: period.startDate,
        endDate: period.endDate,
        compareStartDate: period.compareStartDate,
        compareEndDate: period.compareEndDate,
        limit: expanded ? EXPANDED_LIMIT : DEFAULT_LIMIT,
        offset: 0,
      });
      if (requestId !== requestIdRef.current) return;
      const nextRankings = data?.rankings ?? null;
      cacheRef.current.set(cacheKey, { rankings: nextRankings });
      setRankings(nextRankings);
      onLoaded?.(Date.now());
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error(`Failed to fetch AABI top ${activeDimension}`, err);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    setExpanded(false);
  }, [scopeKey]);

  const lastRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    const forced = lastRefreshKeyRef.current !== refreshKey;
    lastRefreshKeyRef.current = refreshKey;
    loadData({ force: forced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, refreshKey]);

  // Selected-measure ranking comes straight from the dual-ranking payload;
  // share is recomputed from the scoped drill total (§8.4), never from the
  // contract's Current-Sales-only shareOfTotalRevenue.
  const scopedTotal = totals?.[metricKey] ?? 0;
  const rows = useMemo(
    () => rankings?.[measure]?.rows ?? [],
    [rankings, measure],
  );
  // Each ranking keeps only the rows positive in its own measure, so the
  // Realized list can be shorter than the Current one: "View all" counts the
  // ranking on screen.
  const totalCount = rankings?.[measure]?.totalCount ?? 0;

  const renderRow = (row) => {
    if (!row) return null;
    const value = row?.[metricKey] ?? 0;
    const growth = row?.growth?.[metricKey] ?? null;
    const share = scopedTotal > 0 ? value / scopedTotal : 0;
    // Share of the scoped total, exactly like the portfolio bars. Scaling to
    // the largest row instead made an identical-looking bar mean something
    // different in each panel, and always pinned the top row to 100%.
    const barWidth = Math.min(100, share * 100);

    const body = (
      <>
        <div className="aabi-ranked-row-main">
          <Text variant="tinyText" color="darkText">
            {row.name}
          </Text>
          <Text variant="tinyText" color="lightText">
            {`${(share * 100).toFixed(1)}%`}
          </Text>
        </div>
        <div className="aabi-ranked-bar-track" aria-hidden="true">
          <span className="aabi-ranked-bar" style={{ width: `${barWidth}%` }} />
        </div>
        <div className="aabi-ranked-row-secondary">
          <CompactCurrency
            value={value}
            variant="tinyText"
            color="darkText"
          />
          <ChangeIndicator
            variant={growthVariant(growth)}
            percent={formatGrowth(growth)}
          />
        </div>
      </>
    );

    // Every leader row opens the invoice dashboard pre-filtered to that
    // account / state / distributor, within the current drill scope and
    // period. Rows without an id have nothing to filter by, so stay inert.
    const hasId = row.id !== undefined && row.id !== null && row.id !== "";
    const href = !hasId
      ? null
      : buildRowLink
        ? buildRowLink(row, activeDimension)
        : buildInvoiceDashboardUrl({
            manufacturerId: scope.manufacturerId,
            categoryId: scope.categoryId,
            groupId: scope.groupId,
            productId: scope.productId,
            ...{ [ROW_FILTER_KEY[activeDimension]]: row.id },
            startDate: period.startDate,
            endDate: period.endDate,
          });
    if (href) {
      return (
        <li key={`${row.id}-${row.name}`} className="aabi-ranked-item">
          <Link className="aabi-ranked-row" to={href}>
            {body}
          </Link>
        </li>
      );
    }
    return (
      <li key={`${row.id}-${row.name}`} className="aabi-ranked-item">
        <div className="aabi-ranked-row">{body}</div>
      </li>
    );
  };

  return (
    <Panel
      className="aabi-leaders"
      title="Leaders"
      aria-label="Leaders"
      aria-busy={loading}
      actions={
        <>
          {dimensions.map((dimension) => (
            <Button
              key={dimension.key}
              variant="timeframe"
              isActive={activeDimension === dimension.key}
              onClick={() => setActiveDimension(dimension.key)}
            >
              {dimension.label}
            </Button>
          ))}
          {!loading && totalCount > (expanded ? EXPANDED_LIMIT : DEFAULT_LIMIT) && (
            <Button variant="link" onClick={() => setExpanded((prev) => !prev)}>
              <Text variant="smallText" color="primaryColor">
                {expanded ? "Show top 5" : `View all (${totalCount})`}
              </Text>
            </Button>
          )}
        </>
      }
    >
      {error ? (
        <AABIErrorPanel
          message={`Couldn't load ${
            (() => {
              const active = dimensions.find((dimension) => dimension.key === activeDimension);
              return active?.errorLabel ?? active?.label ?? "leaders";
            })()
          }.`}
          onRetry={() => loadData({ force: true })}
        />
      ) : loading ? (
        <RankedListSkeleton rows={DEFAULT_LIMIT} />
      ) : rows.length === 0 ? (
        <div className="aabi-empty-state">
          <Text variant="smallText" color="lightText">
            {emptyMessages[activeDimension]}
          </Text>
        </div>
      ) : (
        <ol className="aabi-ranked-list">{rows.map(renderRow)}</ol>
      )}
    </Panel>
  );
};

export default LeadersPanel;
